import type {
  AudioRecBridgeTuning,
  AudioRecCalibrationProfile,
  AudioRecRoomProfile,
  AudioRecNoteTemplate,
} from '../types';
import {
  average,
  captureDecayRatio,
  clamp,
  flattenCaptureValues,
  median,
  percentile,
  round,
} from './stats';
import type { CalibrationCaptures } from './types';
import { buildNoteTemplates, buildRoomProfile } from './templates';

const CALIBRATION_PROFILE_VERSION = 2;
const MIN_PROFILE_ON_RMS_BASE = 0.0065;
const MIN_PROFILE_KEEP_RMS_BASE = 0.0052;

export interface CalibrationProfileDefaults {
  noiseFloorBase: number;
  hpsMinHz: number;
  hpsMaxHz: number;
}

export const buildCalibrationProfile = (
  captures: CalibrationCaptures,
  currentRmsSensitivity: number,
  defaults: CalibrationProfileDefaults,
): AudioRecCalibrationProfile => {
  const noiseRms = flattenCaptureValues(captures.noise, (capture) => capture.rms);
  const singleRms = flattenCaptureValues(captures.single, (capture) => capture.rms);
  const chordRms = flattenCaptureValues(captures.chord, (capture) => capture.rms);
  const signalRms = [...singleRms, ...chordRms];

  const noiseFloorBase = clamp(
    percentile(noiseRms, 90) ?? defaults.noiseFloorBase,
    defaults.noiseFloorBase * 0.75,
    0.02,
  );

  const signalMedian = median(signalRms) ?? noiseFloorBase * 8;
  const snr = signalMedian / Math.max(noiseFloorBase, 1e-6);

  const minRmsMultiplier = clamp(snr < 3 ? 4.3 : snr < 5 ? 3.7 : snr < 8 ? 3.2 : 2.7, 2.4, 4.8);
  const targetMinRms = clamp(signalMedian * 0.3, noiseFloorBase * 2.4, signalMedian * 0.72);
  const rmsSensitivity =
    signalRms.length < 10
      ? currentRmsSensitivity
      : clamp(targetMinRms / Math.max(noiseFloorBase * minRmsMultiplier, 1e-6), 0.6, 2.1);

  const singleSecondRatios = flattenCaptureValues(
    captures.single,
    (capture) => capture.secondRatios,
  );
  const chordSecondRatios = flattenCaptureValues(captures.chord, (capture) => capture.secondRatios);

  const singleRatioP85 = percentile(singleSecondRatios, 85) ?? 0.14;
  const chordRatioP20 = percentile(chordSecondRatios, 20) ?? singleRatioP85 + 0.06;

  let minPeakAmpRatio =
    chordSecondRatios.length > 0
      ? clamp((singleRatioP85 + chordRatioP20) / 2, 0.08, 0.35)
      : clamp(singleRatioP85 * 1.2, 0.12, 0.32);
  if (snr < 3) {
    minPeakAmpRatio = clamp(minPeakAmpRatio + 0.04, 0.12, 0.35);
  }

  const singlePeakCounts = flattenCaptureValues(captures.single, (capture) => capture.peakCounts);
  const avgSinglePeakCount = average(singlePeakCounts) ?? 1;
  const hpsHarmonics = Math.round(
    clamp(snr < 3 ? 3 : avgSinglePeakCount >= 2.8 && snr >= 4 ? 5 : 4, 3, 5),
  );
  let hpsPeakDrop = clamp(snr < 3 ? 3.8 : snr < 5 ? 4.6 : snr < 8 ? 5.4 : 6.2, 3.2, 7.2);
  if (chordSecondRatios.length > 15) {
    hpsPeakDrop = clamp(hpsPeakDrop + 0.4, 3.2, 7.2);
  }

  const dominantHzs = flattenCaptureValues(captures.single, (capture) => capture.dominantHzs);
  const allHzs = [
    ...flattenCaptureValues(captures.single, (capture) => capture.allHzs),
    ...flattenCaptureValues(captures.chord, (capture) => capture.allHzs),
  ];
  const hpsMinHz = clamp((percentile(dominantHzs, 5) ?? defaults.hpsMinHz) * 0.75, 35, 180);
  const hpsMaxHz = clamp((percentile(allHzs, 98) ?? defaults.hpsMaxHz) * 1.2, 500, 2400);

  const decayRatios = captures.single
    .map((capture) => captureDecayRatio(capture))
    .filter((value): value is number => value != null && Number.isFinite(value));
  const releaseShape = median(decayRatios) ?? 0.45;

  const bridge: AudioRecBridgeTuning = {
    onRmsBase: clamp(noiseFloorBase * 2.8, MIN_PROFILE_ON_RMS_BASE, 0.02),
    keepRmsBase: clamp(noiseFloorBase * 1.9, MIN_PROFILE_KEEP_RMS_BASE, 0.018),
    onStabilityMs: Math.round(clamp(snr < 4 ? 115 : snr < 7 ? 90 : 70, 60, 140)),
    offSilenceMs: Math.round(clamp(120 + releaseShape * 190, 120, 320)),
    minHoldMs: Math.round(clamp(90 + releaseShape * 150, 90, 260)),
    envAttackSec: clamp(snr < 4 ? 0.032 : 0.024, 0.018, 0.04),
    envReleaseSec: clamp(0.12 + releaseShape * 0.22, 0.12, 0.34),
    keepGateRatio: clamp(chordSecondRatios.length > 10 ? 0.62 : 0.68, 0.55, 0.8),
  };

  const roomProfile = buildRoomProfile(captures);
  const noteTemplates = buildNoteTemplates(captures);
  const chordEvidenceFrames = captures.chord.reduce((sum, capture) => {
    const multi = capture.peakCounts.filter((count) => count >= 2).length;
    return sum + multi;
  }, 0);

  return {
    version: CALIBRATION_PROFILE_VERSION,
    createdAtIso: new Date().toISOString(),
    label: chordEvidenceFrames >= 20 ? 'single + chord calibrated' : 'single-note calibrated',
    rmsSensitivity: round(rmsSensitivity, 3),
    noiseFloorBase: round(noiseFloorBase, 7),
    minRmsMultiplier: round(minRmsMultiplier, 3),
    minPeakAmpRatio: round(minPeakAmpRatio, 3),
    hpsPeakDrop: round(hpsPeakDrop, 3),
    hpsHarmonics,
    hpsMinHz: round(hpsMinHz, 2),
    hpsMaxHz: round(hpsMaxHz, 2),
    bridge: {
      onRmsBase: round(bridge.onRmsBase, 7),
      keepRmsBase: round(bridge.keepRmsBase, 7),
      onStabilityMs: bridge.onStabilityMs,
      offSilenceMs: bridge.offSilenceMs,
      minHoldMs: bridge.minHoldMs,
      envAttackSec: round(bridge.envAttackSec, 4),
      envReleaseSec: round(bridge.envReleaseSec, 4),
      keepGateRatio: round(bridge.keepGateRatio, 3),
    },
    roomProfile,
    noteTemplates,
  };
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const sanitizeBridge = (raw: unknown): AudioRecBridgeTuning | null => {
  if (!raw || typeof raw !== 'object') return null;
  const maybe = raw as Partial<AudioRecBridgeTuning>;
  if (
    !isFiniteNumber(maybe.onRmsBase) ||
    !isFiniteNumber(maybe.keepRmsBase) ||
    !isFiniteNumber(maybe.onStabilityMs) ||
    !isFiniteNumber(maybe.offSilenceMs) ||
    !isFiniteNumber(maybe.minHoldMs) ||
    !isFiniteNumber(maybe.envAttackSec) ||
    !isFiniteNumber(maybe.envReleaseSec) ||
    !isFiniteNumber(maybe.keepGateRatio)
  ) {
    return null;
  }

  return {
    onRmsBase: clamp(maybe.onRmsBase, MIN_PROFILE_ON_RMS_BASE, 0.03),
    keepRmsBase: clamp(maybe.keepRmsBase, MIN_PROFILE_KEEP_RMS_BASE, 0.03),
    onStabilityMs: Math.round(clamp(maybe.onStabilityMs, 40, 250)),
    offSilenceMs: Math.round(clamp(maybe.offSilenceMs, 60, 450)),
    minHoldMs: Math.round(clamp(maybe.minHoldMs, 40, 400)),
    envAttackSec: clamp(maybe.envAttackSec, 0.01, 0.08),
    envReleaseSec: clamp(maybe.envReleaseSec, 0.05, 0.6),
    keepGateRatio: clamp(maybe.keepGateRatio, 0.4, 0.9),
  };
};

const sanitizeRoomProfile = (raw: unknown): AudioRecRoomProfile => {
  if (!raw || typeof raw !== 'object') return { noisePeaks: [] };
  const maybe = raw as Partial<AudioRecRoomProfile>;
  const noisePeaks = Array.isArray(maybe.noisePeaks) ? maybe.noisePeaks : [];
  return {
    noisePeaks: noisePeaks
      .filter(
        (peak): peak is NonNullable<AudioRecRoomProfile['noisePeaks'][number]> =>
          !!peak && isFiniteNumber(peak.hz) && isFiniteNumber(peak.strength),
      )
      .map((peak) => ({
        hz: clamp(peak.hz, 20, 5000),
        strength: clamp(peak.strength, 0, 3),
      }))
      .slice(0, 12),
  };
};

const sanitizeNoteTemplates = (raw: unknown): AudioRecNoteTemplate[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(
      (template): template is Partial<AudioRecNoteTemplate> =>
        !!template &&
        isFiniteNumber(template.midi) &&
        typeof template.note === 'string' &&
        isFiniteNumber(template.fundamentalHz) &&
        isFiniteNumber(template.centsOffset) &&
        isFiniteNumber(template.sampleCount) &&
        isFiniteNumber(template.medianRms) &&
        isFiniteNumber(template.decayRatio) &&
        Array.isArray(template.harmonics) &&
        Array.isArray(template.aliases),
    )
    .map((template) => ({
      midi: Math.round(clamp(template.midi as number, 0, 127)),
      note: template.note as string,
      fundamentalHz: clamp(template.fundamentalHz as number, 20, 5000),
      centsOffset: clamp(template.centsOffset as number, -150, 150),
      sampleCount: Math.round(clamp(template.sampleCount as number, 1, 128)),
      medianRms: clamp(template.medianRms as number, 0, 1),
      decayRatio: clamp(template.decayRatio as number, 0.02, 2),
      harmonics: (template.harmonics as AudioRecNoteTemplate['harmonics'])
        .filter(
          (harmonic) =>
            isFiniteNumber(harmonic.harmonic) &&
            isFiniteNumber(harmonic.amplitudeRatio) &&
            isFiniteNumber(harmonic.centsOffset) &&
            isFiniteNumber(harmonic.coverage),
        )
        .map((harmonic) => ({
          harmonic: Math.round(clamp(harmonic.harmonic, 1, 12)),
          amplitudeRatio: clamp(harmonic.amplitudeRatio, 0, 2.5),
          centsOffset: clamp(harmonic.centsOffset, -150, 150),
          coverage: clamp(harmonic.coverage, 0, 1),
        }))
        .slice(0, 12),
      aliases: (template.aliases as AudioRecNoteTemplate['aliases'])
        .filter(
          (alias) =>
            isFiniteNumber(alias.midi) &&
            typeof alias.note === 'string' &&
            isFiniteNumber(alias.avgHz) &&
            isFiniteNumber(alias.strength),
        )
        .map((alias) => ({
          midi: Math.round(clamp(alias.midi, 0, 127)),
          note: alias.note,
          avgHz: clamp(alias.avgHz, 20, 5000),
          strength: clamp(alias.strength, 0, 3),
        }))
        .slice(0, 12),
    }))
    .slice(0, 24);
};

export const sanitizeCalibrationProfile = (raw: unknown): AudioRecCalibrationProfile | null => {
  if (!raw || typeof raw !== 'object') return null;
  const maybe = raw as Partial<AudioRecCalibrationProfile>;
  const bridge = sanitizeBridge(maybe.bridge);
  if (
    !isFiniteNumber(maybe.version) ||
    typeof maybe.createdAtIso !== 'string' ||
    typeof maybe.label !== 'string' ||
    !isFiniteNumber(maybe.rmsSensitivity) ||
    !isFiniteNumber(maybe.noiseFloorBase) ||
    !isFiniteNumber(maybe.minRmsMultiplier) ||
    !isFiniteNumber(maybe.minPeakAmpRatio) ||
    !isFiniteNumber(maybe.hpsPeakDrop) ||
    !isFiniteNumber(maybe.hpsHarmonics) ||
    !isFiniteNumber(maybe.hpsMinHz) ||
    !isFiniteNumber(maybe.hpsMaxHz) ||
    !bridge
  ) {
    return null;
  }

  return {
    version: Math.round(maybe.version),
    createdAtIso: maybe.createdAtIso,
    label: maybe.label,
    rmsSensitivity: clamp(maybe.rmsSensitivity, 0.5, 2.5),
    noiseFloorBase: clamp(maybe.noiseFloorBase, 0.00001, 0.03),
    minRmsMultiplier: clamp(maybe.minRmsMultiplier, 1.5, 6),
    minPeakAmpRatio: clamp(maybe.minPeakAmpRatio, 0.05, 0.45),
    hpsPeakDrop: clamp(maybe.hpsPeakDrop, 2.5, 8),
    hpsHarmonics: Math.round(clamp(maybe.hpsHarmonics, 2, 6)),
    hpsMinHz: clamp(maybe.hpsMinHz, 20, 500),
    hpsMaxHz: clamp(maybe.hpsMaxHz, 300, 4000),
    bridge,
    roomProfile: sanitizeRoomProfile((maybe as { roomProfile?: unknown }).roomProfile),
    noteTemplates: sanitizeNoteTemplates((maybe as { noteTemplates?: unknown }).noteTemplates),
  };
};
